import { EnemyAI, FightingTriggers, IdleBehaviours, FightingBehaviours, LeashTriggers, LeashingBehaviours, AttackTypes, DeadBehaviours, ShotLeadPrecision, EnemyType, EnemyTargetingStyles } from '../../shared/ai-types'
import { BurstFireModes } from '../../../projectiles/shared/projectile-types'
import { SpineDataName, EnemyBipedSkinType } from '../../../models-animations/shared/spine-config'
import { EnemyDefaults } from './enemy-defaults'
import { Colors } from '../../../utils/colors'
import { ParticleEffectType } from '../../../engine/shared/game-data/particle-config'
import { Vector } from 'sat'
import { ColliderTraits, ColliderType } from '../../../collision/shared/colliders'
import { ENEMY_DEFAULT_HEALTH } from '../../../engine/shared/game-data/enemy-formulas'
import { Ability, AbilitySelectionStyles, AbilityTargets, AbilityTargetSelectionStyle, AbilityType, ActionCriteria, CriteriaSatisfactionQuantity, CriteriaTarget, CriteriaType } from '../action-types'
import { alwaysTrueCriteria } from './abilities/common-abilities'
import { BuffIdentifier } from '../../../buffs/shared/buff.shared'
import { attackRatedAbilityList } from '../action-criteria/ability-helpers'
import { AnimationTrack } from '../../../models-animations/shared/animation-track'
import { ENEMY_NAMES } from './enemy-names'
import { worldDifficultyHardCriterion, worldDifficultyBrutalCriterion, worldDifficultyBrutalCriteria } from '../action-criteria/action-criteria-helpers'
import { WorldDifficulty } from '../../../engine/shared/game-data/world-difficulty'

export const hermitShootAbility: Ability = {
	debugName: 'shoot',
	abilityType: AbilityType.FIRE_PROJECTILE,
	durationInGameTicks: 0,
	validTargets: AbilityTargets.ALL_NEARBY_ALIVE_PLAYERS,
	validTargetSelection: AbilityTargetSelectionStyle.RANDOM,
	projectileConfig: {
		...EnemyDefaults.projectileConfig,
		speed: 500,
		colliderRadius: 30,
		baseLightningDamage: 100,
		lifespanInSeconds: 6,
		burstMode: BurstFireModes.STRAIGHT,
		color: Colors.green,
		bulletParticleEffect: ParticleEffectType.PROJECTILE_HEAD_BOSS,
		bulletTrailParticleEffect: ParticleEffectType.PROJECTILE_LIGHTNING_TRAIL,
		muzzleFlairParticleEffect: ParticleEffectType.PROJECTILE_LIGHTNING_SHOOT,
	},
}

const doesNotHaveBuffCriteria: ActionCriteria = {
	satisfactionQuantity: CriteriaSatisfactionQuantity.ALL,
	criterias: [
		{
			criteriaTargets: CriteriaTarget.SELF,
			criteriaType: CriteriaType.NO_PRESENCE_OF_BUFF_OR_DEBUFF,
			criteriaValue: BuffIdentifier.HermitCrabBlock,
			minimumTargetsThatSatisfy: 1,
		},
	],
}

export const hermitBlockAbility: Ability = {
	debugName: 'block',
	abilityType: AbilityType.INSTANTLY_APPLY_EFFECT,
	buffToApply: BuffIdentifier.HermitCrabBlock,
	durationInGameTicks: 0,
	validTargets: AbilityTargets.MYSELF,
	validTargetSelection: AbilityTargetSelectionStyle.ALL,
}

export const cooldownEnemyCooldown15000: Ability = {
	debugName: 'cooldown15000',
	abilityType: AbilityType.INSTANTLY_APPLY_EFFECT,
	buffToApply: BuffIdentifier.EnemyCooldown15000,
	durationInGameTicks: 0,
	validTargets: AbilityTargets.MYSELF,
	validTargetSelection: AbilityTargetSelectionStyle.ALL,
}

const animationTimes = {
	[AnimationTrack.SHOOT]: 0.3,
}

const hermitCrab: EnemyAI = {
	name: ENEMY_NAMES.HERMIT_CRAB,
	type: EnemyType.BASIC,
	appearance: {
		asset: SpineDataName.HERMIT_CRAB,
		skin: EnemyBipedSkinType.DEFAULT,
		mixSettings: [],
	},
	general: {
		enemyLevel: 1,
		experienceWhenKilled: 0.35,
	},
	baseAttributes: {
		...EnemyDefaults.baseAttributes,
		visibilityRadius: 1,
		colliders: [
			{
				type: ColliderType.Circle,
				radius: 40,
				traits: ColliderTraits.BlockMovementAndProjectile,
				position: [0, -20],
			},
		],
		movementSpeed: 100,
		decelerationRate: 10,
		turningRatePerSecondInDegrees: 300,
		attackOffset: new Vector(0, 0),
		lootDropOffset: new Vector(0, 0),
		damageConfig: null,

		physicalDamage: 0,
		fireDamage: 0,
		iceDamage: 0,
		lightningDamage: 0,
		poisonDamage: 0,
		critChance: 0,
		critDamage: 1.0,

		maxHealth: (ENEMY_DEFAULT_HEALTH * 2) / 4,
		defense: 0,
		fireResist: 0,
		poisonResist: 0,
		iceResist: 0,
		lightningResist: 0,

		lootDropProbability: 1,
		lootDropQuantity: 1,
		lootDropRarity: 1,
	},
	soundEffects: {
		attack: 'Projectile_Zap',
		impact: 'SFX_Impact_Enemy_Mush',
	},
	states: {
		idling: {
			behaviour: IdleBehaviours.MEANDER,
			targetingStyle: EnemyTargetingStyles.NEAREST,
			targetingCheckSeconds: 2,
			transitionToFighting: {
				trigger: FightingTriggers.AGGRESSIVE_PROXIMITY,
				radius: 900,
			},
			meander: {
				radius: 500,
				speedMultiplier: 1,
				minRestTime: 0.5,
				maxRestTime: 10,
			},
			aggroOnHit: true,
		},
		fighting: {
			behaviour: FightingBehaviours.CHASE_AND_ATTACK,
			targetingStyle: EnemyTargetingStyles.HIGH_HEALTH,
			brain: {
				actions: [
					
					{
						// BLOCK/ATTACK
						priority: 2,
						actionCriteria: {
							satisfactionQuantity: CriteriaSatisfactionQuantity.ALL,
							criterias: [
								worldDifficultyBrutalCriterion,
								{
									criteriaTargets: CriteriaTarget.SELF,
									criteriaType: CriteriaType.NO_PRESENCE_OF_BUFF_OR_DEBUFF,
									criteriaValue: BuffIdentifier.HermitCrabBlock,
									minimumTargetsThatSatisfy: 1,
								},
							],
						},

						actionAbilities: {
							abilitySelectionStyle: AbilitySelectionStyles.WEIGHTED_RANDOM,
							abilityOptions: [
								[0.5, hermitBlockAbility, 15],
								//[0.5, hermitShootAbility, 20],
								attackRatedAbilityList(1, animationTimes, AnimationTrack.SHOOT, hermitShootAbility, 15),
							],
						},
					},
					{
						// ATTACK
						priority: 2.1,
						actionCriteria:  worldDifficultyBrutalCriteria,
						actionAbilities: {
							abilitySelectionStyle: AbilitySelectionStyles.SINGLE,
							abilityOptions: [[0, hermitShootAbility, 15]],
						},
					},
					{
						// BLOCK/ATTACK
						priority: 3,
						actionCriteria: {
							satisfactionQuantity: CriteriaSatisfactionQuantity.ALL,
							criterias: [
								worldDifficultyHardCriterion,
								{
									criteriaTargets: CriteriaTarget.SELF,
									criteriaType: CriteriaType.NO_PRESENCE_OF_BUFF_OR_DEBUFF,
									criteriaValue: BuffIdentifier.HermitCrabBlock,
									minimumTargetsThatSatisfy: 1,
								},
							],
						},

						actionAbilities: {
							abilitySelectionStyle: AbilitySelectionStyles.WEIGHTED_RANDOM,
							abilityOptions: [
								[0.5, hermitBlockAbility, 20],
								//[0.5, hermitShootAbility, 20],
								attackRatedAbilityList(1, animationTimes, AnimationTrack.SHOOT, hermitShootAbility, 20),
							],
						},
					},
					{
						// BLOCK/ATTACK
						priority: 4,
						actionCriteria: {
							satisfactionQuantity: CriteriaSatisfactionQuantity.ALL,
							criterias: [
								{
									criteriaTargets: CriteriaTarget.SELF,
									criteriaType: CriteriaType.WORLD_DIFFICULTY_AT_OR_BELOW_THRESHOLD,
									criteriaValue: WorldDifficulty.Hard,
									minimumTargetsThatSatisfy: 1,
								},
								{
									criteriaTargets: CriteriaTarget.SELF,
									criteriaType: CriteriaType.NO_PRESENCE_OF_BUFF_OR_DEBUFF,
									criteriaValue: BuffIdentifier.HermitCrabBlock,
									minimumTargetsThatSatisfy: 1,
								},
								{
									criteriaTargets: CriteriaTarget.SELF,
									criteriaType: CriteriaType.NO_PRESENCE_OF_BUFF_OR_DEBUFF,
									criteriaValue: BuffIdentifier.EnemyCooldown15000,
									minimumTargetsThatSatisfy: 1,
								},
							],
						},

						actionAbilities: {
							abilitySelectionStyle: AbilitySelectionStyles.SEQUENCE,
							abilityOptions: [
								[0.5, hermitBlockAbility, 0, cooldownEnemyCooldown15000, 20],
								//[0.5, hermitShootAbility, 20],
								attackRatedAbilityList(1, animationTimes, AnimationTrack.SHOOT, hermitShootAbility, 20),
							],
						},
					},
					{
						// ATTACK
						priority: 10,
						actionCriteria: alwaysTrueCriteria,
						actionAbilities: {
							abilitySelectionStyle: AbilitySelectionStyles.SINGLE,
							abilityOptions: [[0, hermitShootAbility, 20]],
						},
					},
				],
			},
			targetingCheckSeconds: 0,
			attackType: AttackTypes.PROJECTILE,
			engagementMaxDistance: 1200,
			engagementMinDistance: 0,
			modelCenterOffset: 0,
			movementMaxDistance: 600,
			movementMinDistance: 0,
			attackOptions: [],
			shotLeadPrecision: ShotLeadPrecision.NONE,
			visualAimLockSeconds: 0.1,
			transitionToLeashing: {
				trigger: LeashTriggers.RADIUS_EXCEEDED,
				radius: 2000,
				distanceTravelled: 4000,
			},
		},
		leashing: {
			behaviour: LeashingBehaviours.MOVE,
			speedMultiplier: 4,
		},
		fleeing: {},
		dead: {
			behaviour: DeadBehaviours.BE_A_CORPSE,
			corpseTimeoutInSeconds: 1,
		},
	},
}

export default hermitCrab
